import React, { Suspense, useMemo } from 'react'

import { Await, useAsyncValue, useLoaderData, useRevalidator } from 'react-router-dom'

import AwaitWithRedirect from '@components/AwaitWithRedirect'
import CollectionView from '@components/myScores/CollectionView'
import ImageUploader from '@components/shared/ImageUploader'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import { CollectionLoaderResponse, CollectionResponse } from '@pages/home/loaders/collection.loader'
import { usePageTitle } from '@providers/LayoutProvider'
import { updateCollectionImage, UploadCollectionImageResponse } from '@services/CollectionService'
import { getProfileEpoch, shouldUpdateProfileEpoch } from '@utils/helpers'

const CollectionPage: React.FC = () => {
  const data = useLoaderData() as CollectionLoaderResponse
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AwaitWithRedirect
        resolve={data.collection}
        redirects={[{ redirectUrl: '/home', errorCode: 'collectionNotFound' }]}>
        <CollectionPageBody />
      </AwaitWithRedirect>
    </Suspense>
  )
}

export default CollectionPage

const CollectionPageBody: React.FC = () => {
  const data = useLoaderData() as CollectionLoaderResponse
  const { collection } = useAsyncValue() as CollectionResponse
  const revalidator = useRevalidator()

  usePageTitle(`${collection.name} Collection`, [collection.name])

  const profileEpoch = getProfileEpoch()
  const collectionImage = useMemo(() => {
    let uri = `/collections/image/${collection.id}`

    // Cache-bust the URL if it's the current user, so they can see changes immediately
    if (profileEpoch) {
      uri += `?${profileEpoch}`
    }
    return uri
  }, [collection, profileEpoch])

  return (
    <>
      <ImageUploader
        imageUrl={collectionImage}
        blurHash={collection.blur_hash}
        labelKey="action.changeCover"
        className="max-h-[230px] md:max-h-[280px] bg-white mt-4 md:mt-10"
        aspect={16 / 9.0}
        overlayContent={
          <div className="flex flex-row items-end inset-0 absolute">
            <div className="text-white m-4 md:m-10 w-full text-shadow min-w-0">
              <p className="text-h2 sm:text-[3.5rem] sm:leading-[4rem] md:text-h1 font-semibold md:leading-[5rem] ellipsify-2">
                {collection.name}
              </p>
              {collection.description && (
                <p className="text-lg font-medium mt-1">{collection.description}</p>
              )}
            </div>
          </div>
        }
        onUpload={async (resp: UploadCollectionImageResponse, blurHash: string | null) => {
          if (!resp.success) return false

          const data = await updateCollectionImage(
            collection.id,
            resp.collection_image_id,
            blurHash
          )
          if (!data) return false

          shouldUpdateProfileEpoch(true)
          revalidator.revalidate()
          return true
        }}
      />
      <Suspense fallback={<LoadingSpinner />}>
        <Await resolve={data.items}>
          {(itemsResponse) => (
            <CollectionView collection={collection} initialData={itemsResponse} />
          )}
        </Await>
      </Suspense>
    </>
  )
}
